import { SetStateAction, useCallback, useMemo, useState } from 'react';

export type UseConditionallySynchronizedStateProps<T> = {
    value: T;
    /**
     * Whether internal value should be automatically updated to match `value`
     */
    synchronize: boolean;
    /**
     * Determines value of `a` and `b` are different.
     *
     * Optional. Default is `Object.is`, which checks reference equality for object types
     */
    valueComparer?: (a: T, b: T) => boolean;
};

/**
 * Creates duplicate of `value` which is kept synchronized with the latest value of `value` while `synchronize` is true.
 *
 * Value can be manually updated via returned setter function while `synchronize` is false.
 */
export function useConditionallySynchronizedState<T>({
    value,
    synchronize,
    valueComparer = Object.is,
}: UseConditionallySynchronizedStateProps<T>) {
    const [syncedValue, setSyncedValue] = useState(() => value);

    if (synchronize && !valueComparer(syncedValue, value)) {
        // This causes the current render to restart, instead of causing a rerender (like useEffect would)
        setSyncedValue(value);
    }

    const setValue = useCallback(
        (next: SetStateAction<T>) => {
            if (!synchronize) {
                setSyncedValue(next);
            }
        },
        [synchronize],
    );

    return useMemo(() => [syncedValue, setValue] as const, [syncedValue, setValue]);
}
